import { useState } from 'react';
import styled from 'styled-components';
import { usePutGeoSurveyMutation, useUploadGeoSurveyMutation } from '@shared/api';
import {
  COLUMN_ORDERS,
  fmtCoord,
  fmtDateTime,
  parsePointRole,
  SURVEY_ROLES,
  surveyRoleLabel,
  type GeoColumnOrder,
  type GeoSurveyPoint,
  type GeoSurveyUploadMode,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Input, Select } from '@shared/ui/Input';
import { IconPencil, IconPlus, IconTrash } from '@shared/ui/icons';
import {
  AiBadge,
  CellActions,
  CellInput,
  CellSelect,
  Check,
  ChoiceCard,
  ChoiceGrid,
  DataTable,
  ErrorText,
  FieldGrid,
  FieldLabel,
  FieldNote,
  Fieldset,
  IconButton,
  Muted,
  Notice,
  NumInput,
  Panel,
  PanelHead,
  PanelTitle,
  ReasonDialog,
  Row,
  Stack,
  TableWrap,
  ToneRow,
  useAction,
  useDraft,
} from '@widgets/GeoSchemes';
import { useSettingsSaver, useStepDirty, useWizard } from './wizard';
import { ACCEPT_TABLE } from './DesignPanel';
import { codeRuleInputs, SurveyCodes } from './SurveyCodes';

const PAGE = 200;

const Sub = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Details = styled.details`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing(3)};

  summary {
    cursor: pointer;
    font-size: 13.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const EditList = styled.ol`
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  line-height: 1.5;
`;

const EditMeta = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AssistOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const NoticeTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-weight: 600;
`;

function pointText(p: GeoSurveyPoint | null): string {
  if (!p) return '—';
  return `X ${fmtCoord(p.x)}, Y ${fmtCoord(p.y)}${p.h != null ? `, H ${fmtCoord(p.h)}` : ''}`;
}

function rowValid(p: GeoSurveyPoint): boolean {
  return p.name.trim() !== '' && Number.isFinite(p.x) && Number.isFinite(p.y);
}

/** Карточка «Геодезическая съёмка»: файл, прибор, точки с правкой по причине. */
export function SurveyPanel() {
  const { scheme, readOnly } = useWizard();
  const pts = useDraft(scheme.survey.points);
  const instrument = useDraft(scheme.settings.instrument);
  // Съёмка хранится в JSONB: у загруженной раньше правил кодов и сведений о разборе нет.
  const codeRules = scheme.survey.code_rules ?? [];
  const parseInfo = scheme.survey.parse_info ?? null;
  const codes = useDraft(codeRuleInputs(codeRules));
  useStepDirty('survey', pts.dirty && !readOnly);
  useStepDirty('survey-codes', codes.dirty && !readOnly);
  useStepDirty('instrument', instrument.dirty && !readOnly);
  const [upload] = useUploadGeoSurveyMutation();
  const [put] = usePutGeoSurveyMutation();
  const saver = useSettingsSaver();
  const uploading = useAction();
  const saving = useAction();

  const [order, setOrder] = useState<GeoColumnOrder>(scheme.survey.column_order);
  const [mode, setMode] = useState<GeoSurveyUploadMode>('replace');
  const [assist, setAssist] = useState(true);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [row, setRow] = useState<GeoSurveyPoint | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [askReason, setAskReason] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  const list = pts.draft;

  function onUpload(file: File) {
    if (
      (pts.dirty || codes.dirty) &&
      !window.confirm('Есть несохранённые правки точек или кодов — они пропадут. Продолжить?')
    )
      return;
    void uploading
      .run(
        () =>
          upload({
            id: scheme.id,
            file,
            column_order: order,
            mode,
            assist: assist ? 'auto' : 'off',
          }).unwrap(),
        'Файл съёмки не загрузился.',
      )
      .then((ok) => {
        if (!ok) return;
        pts.reset();
        codes.reset();
      });
  }

  function startEdit(i: number) {
    const p = list[i];
    if (!p) return;
    setEditIdx(i);
    setRow(p);
    setIsNew(false);
  }

  function commitRow() {
    if (editIdx == null || !row || !rowValid(row)) return;
    const next = { ...row, name: row.name.trim(), code: row.code?.trim() ? row.code.trim() : null };
    pts.update((prev) => prev.map((p, i) => (i === editIdx ? next : p)));
    setEditIdx(null);
    setRow(null);
    setIsNew(false);
  }

  function cancelRow() {
    if (isNew && editIdx != null) pts.update((prev) => prev.filter((_, i) => i !== editIdx));
    setEditIdx(null);
    setRow(null);
    setIsNew(false);
  }

  function addRow() {
    const p: GeoSurveyPoint = {
      name: '',
      x: Number.NaN,
      y: Number.NaN,
      h: null,
      code: null,
      role: 'single',
    };
    pts.update((prev) => [p, ...prev]);
    setEditIdx(0);
    setRow(p);
    setIsNew(true);
  }

  async function savePoints(reason: string) {
    const ok = await saving.run(
      () => put({ id: scheme.id, points: list.filter(rowValid), reason }).unwrap(),
      'Точки не сохранены.',
    );
    if (ok) {
      pts.reset();
      setAskReason(false);
    }
  }

  async function saveInstrument() {
    const ok = await saver.save((cur) => ({ ...cur, instrument: instrument.draft }));
    if (ok) instrument.reset();
  }

  const invalidCount = list.filter((p) => !rowValid(p)).length;
  const edits = scheme.survey.edits.slice().sort((a, b) => (a.at < b.at ? 1 : -1));

  return (
    <Panel id="geo-survey" aria-labelledby="geo-survey-title" tabIndex={-1}>
      <PanelHead>
        <PanelTitle id="geo-survey-title">Геодезическая съёмка</PanelTitle>
        <Muted>
          {list.length} точ.{scheme.survey.file_name ? ` · ${scheme.survey.file_name}` : ''}
        </Muted>
      </PanelHead>
      <Stack $gap={4}>
        {!readOnly && (
          <Stack $gap={3}>
            <FieldGrid $min={260}>
              <FieldLabel>
                Порядок колонок
                <Select
                  value={order}
                  onChange={(e) => setOrder(e.target.value === 'nyxhc' ? 'nyxhc' : 'nxyhc')}
                >
                  {COLUMN_ORDERS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </FieldLabel>
            </FieldGrid>
            <ChoiceGrid $min={180} role="radiogroup" aria-label="Как загрузить файл съёмки">
              <ChoiceCard
                name="geo-survey-mode"
                checked={mode === 'replace'}
                onSelect={() => setMode('replace')}
                title="Заменить"
              >
                прежние точки удаляются
              </ChoiceCard>
              <ChoiceCard
                name="geo-survey-mode"
                checked={mode === 'merge'}
                onSelect={() => setMode('merge')}
                title="Дополнить"
              >
                точки с тем же именем обновятся
              </ChoiceCard>
            </ChoiceGrid>
            <AssistOption>
              <Row $gap={2}>
                <Check checked={assist} onChange={setAssist} disabled={uploading.busy}>
                  Разрешить ИИ-помощнику разобрать нестандартный файл
                </Check>
                <AiBadge />
              </Row>
              <FieldNote>
                Если файл не по шаблону (лишние колонки, служебные строки прибора), ИИ-помощник
                предложит разбор, а программа проверит: координаты берутся из файла как есть.
              </FieldNote>
            </AssistOption>
            <FileDropZone
              accept={ACCEPT_TABLE}
              compact
              disabled={uploading.busy}
              primary={uploading.busy ? 'Загружаем съёмку…' : 'Файл съёмки CSV, TXT или XLSX'}
              hint="верх/низ для вертикальности — по суффиксу имени (12в / 12н) или коду В / Н"
              onFile={onUpload}
            />
            {uploading.error && <ErrorText role="alert">{uploading.error}</ErrorText>}
          </Stack>
        )}

        {parseInfo?.method === 'assist' && (
          <Notice $tone="warning" role="status">
            <NoticeTitle>
              <AiBadge />
              Файл нестандартный — разобран с ИИ-помощником
            </NoticeTitle>
            {parseInfo.note && <span>{parseInfo.note}</span>}
            <span>
              Проверьте имена, координаты и коды точек в таблице ниже, прежде чем считать
              отклонения. Ошибку исправьте правкой точки — она попадёт в журнал.
            </span>
          </Notice>
        )}

        {scheme.survey.warnings.length > 0 && (
          <Notice $tone="warning" role="status">
            {scheme.survey.warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </Notice>
        )}

        <SurveyCodes rules={codeRules} codes={codes} pointsDirty={pts.dirty} />

        <Stack $gap={3}>
          <Sub>Прибор и дата съёмки — в примечание схемы</Sub>
          <Fieldset disabled={readOnly}>
            <FieldGrid $min={180}>
              <FieldLabel>
                Модель прибора
                <Input
                  value={instrument.draft.model}
                  onChange={(e) => instrument.set({ ...instrument.draft, model: e.target.value })}
                  placeholder="Тахеометр Leica TS07"
                />
              </FieldLabel>
              <FieldLabel>
                Серийный №
                <Input
                  value={instrument.draft.serial}
                  onChange={(e) => instrument.set({ ...instrument.draft, serial: e.target.value })}
                />
              </FieldLabel>
              <FieldLabel>
                Свидетельство о поверке
                <Input
                  value={instrument.draft.verification}
                  onChange={(e) =>
                    instrument.set({ ...instrument.draft, verification: e.target.value })
                  }
                  placeholder="№ и дата"
                />
              </FieldLabel>
              <FieldLabel>
                Дата съёмки
                <Input
                  type="date"
                  value={instrument.draft.survey_date}
                  onChange={(e) =>
                    instrument.set({ ...instrument.draft, survey_date: e.target.value })
                  }
                />
              </FieldLabel>
            </FieldGrid>
          </Fieldset>
          {instrument.dirty && !readOnly && (
            <Row $gap={2}>
              <Button
                type="button"
                $size="sm"
                onClick={() => void saveInstrument()}
                disabled={saver.busy}
              >
                {saver.busy ? 'Сохранение…' : 'Сохранить сведения о приборе'}
              </Button>
              <Button
                type="button"
                $size="sm"
                $variant="ghost"
                onClick={instrument.reset}
                disabled={saver.busy}
              >
                Отменить
              </Button>
            </Row>
          )}
          {saver.error && <ErrorText role="alert">{saver.error}</ErrorText>}
        </Stack>

        <Row $gap={2}>
          {!readOnly && (
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={addRow}
              disabled={editIdx != null}
            >
              <IconPlus size={16} /> Добавить точку
            </Button>
          )}
          {pts.dirty && !readOnly && (
            <>
              <Button
                type="button"
                $variant="primary"
                $size="sm"
                onClick={() => setAskReason(true)}
                disabled={saving.busy || editIdx != null}
              >
                Сохранить точки…
              </Button>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                onClick={() => {
                  pts.reset();
                  setEditIdx(null);
                  setRow(null);
                }}
              >
                Отменить правки
              </Button>
            </>
          )}
          {invalidCount > 0 && (
            <Muted>Без имени или координат: {invalidCount} — не сохранятся.</Muted>
          )}
        </Row>

        {list.length === 0 ? (
          <Muted>Точек съёмки нет — загрузите файл с прибора.</Muted>
        ) : (
          <TableWrap $maxHeight={460}>
            <DataTable>
              <thead>
                <tr>
                  <th>Имя</th>
                  <th className="num">X, м</th>
                  <th className="num">Y, м</th>
                  <th className="num">H, м</th>
                  <th>Код</th>
                  <th>Роль</th>
                  <th aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {list.slice(0, limit).map((p, i) =>
                  editIdx === i && row ? (
                    <ToneRow key={`edit-${i}`} $tone="warning">
                      <td>
                        <CellInput
                          value={row.name}
                          onChange={(e) => setRow({ ...row, name: e.target.value })}
                          aria-label="Имя точки"
                          aria-invalid={row.name.trim() === ''}
                          autoFocus
                        />
                      </td>
                      <td>
                        <NumInput
                          variant="cell"
                          value={row.x}
                          aria-label="X, м"
                          onValue={(v) => setRow({ ...row, x: v ?? Number.NaN })}
                        />
                      </td>
                      <td>
                        <NumInput
                          variant="cell"
                          value={row.y}
                          aria-label="Y, м"
                          onValue={(v) => setRow({ ...row, y: v ?? Number.NaN })}
                        />
                      </td>
                      <td>
                        <NumInput
                          variant="cell"
                          value={row.h}
                          aria-label="H, м"
                          onValue={(v) => setRow({ ...row, h: v })}
                        />
                      </td>
                      <td>
                        <CellInput
                          value={row.code ?? ''}
                          onChange={(e) => setRow({ ...row, code: e.target.value })}
                          aria-label="Код"
                        />
                      </td>
                      <td>
                        <CellSelect
                          value={row.role}
                          aria-label="Роль точки"
                          onChange={(e) => {
                            const role = parsePointRole(e.target.value);
                            if (role) setRow({ ...row, role });
                          }}
                        >
                          {SURVEY_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </CellSelect>
                      </td>
                      <td>
                        <CellActions>
                          <Button
                            type="button"
                            $size="sm"
                            onClick={commitRow}
                            disabled={!rowValid(row)}
                          >
                            Готово
                          </Button>
                          <Button type="button" $size="sm" $variant="ghost" onClick={cancelRow}>
                            Отмена
                          </Button>
                        </CellActions>
                      </td>
                    </ToneRow>
                  ) : (
                    <ToneRow
                      key={`${p.name}-${i}`}
                      $tone={p.role === 'ignore' ? 'muted' : undefined}
                    >
                      <td>{p.name || '—'}</td>
                      <td className="num">{fmtCoord(p.x)}</td>
                      <td className="num">{fmtCoord(p.y)}</td>
                      <td className="num">{fmtCoord(p.h)}</td>
                      <td>{p.code ?? '—'}</td>
                      <td>{surveyRoleLabel(p.role)}</td>
                      <td>
                        {!readOnly && (
                          <CellActions>
                            <IconButton
                              type="button"
                              aria-label={`Править точку ${p.name}`}
                              onClick={() => startEdit(i)}
                              disabled={editIdx != null}
                            >
                              <IconPencil size={15} />
                            </IconButton>
                            <IconButton
                              type="button"
                              $danger
                              aria-label={`Удалить точку ${p.name}`}
                              onClick={() =>
                                pts.update((prev) => prev.filter((_, idx) => idx !== i))
                              }
                              disabled={editIdx != null}
                            >
                              <IconTrash size={15} />
                            </IconButton>
                          </CellActions>
                        )}
                      </td>
                    </ToneRow>
                  ),
                )}
              </tbody>
            </DataTable>
          </TableWrap>
        )}
        {list.length > limit && (
          <div>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={() => setLimit((n) => n + PAGE)}
            >
              Показать ещё ({list.length - limit})
            </Button>
          </div>
        )}

        {edits.length > 0 && (
          <Details>
            <summary>Журнал правок съёмки · {edits.length}</summary>
            <EditList>
              {edits.map((e, i) => (
                <li key={`${e.at}-${e.point}-${i}`}>
                  <b>{e.point}</b>: {pointText(e.before)} → {pointText(e.after)}
                  <br />
                  <EditMeta>
                    {e.user_name ?? '—'} · {fmtDateTime(e.at)} · причина: {e.reason}
                  </EditMeta>
                </li>
              ))}
            </EditList>
          </Details>
        )}
      </Stack>

      {askReason && (
        <ReasonDialog
          title="Сохранить правки съёмки"
          hint="Ручная правка точек съёмки пишется в журнал схемы: кто, когда, было и стало, по какой причине."
          label="Причина правки (обязательно)"
          confirmLabel="Сохранить точки"
          busy={saving.busy}
          error={saving.error}
          onConfirm={(reason) => void savePoints(reason)}
          onClose={() => setAskReason(false)}
        />
      )}
    </Panel>
  );
}
